// src/redux/api/settingsProfilesApi.ts
import { createApi } from "@reduxjs/toolkit/query/react";
import { privateBaseQuery } from "../base/private/privateBaseQuery";

// Базовый тип настроек профиля. Пока без детализации.
export interface ProctoringProfileSettings {
    [key: string]: any;
}

export interface SettingsProfile {
    id: number;
    type: string;
    name: string;
    description: string;
    level: "low" | "medium" | "high" | string;
    default?: boolean;
    is_default: boolean;
    settings: ProctoringProfileSettings;
}

export interface CreateSettingsProfilePayload {
    type: string;
    assignment_id?: number;
    name: string;
    description: string;
    level: "low" | "medium" | "high" | string;
    default?: boolean;
    settings: ProctoringProfileSettings;
}

export interface UpdateSettingsProfilePayload
    extends CreateSettingsProfilePayload {
    id: number;
    apply_to_assignments?: boolean;
}

export interface AssignmentProctoringProfilesResponse {
    low?: SettingsProfile;
    medium?: SettingsProfile;
    high?: SettingsProfile;
    [key: string]: any;
}

type ApiEnvelope<T> = {
    entity?: T;
    entities?: T[];
};

const unwrapEntity = <T,>(response: unknown): T | null => {
    if (!response || typeof response !== "object") return null;

    const maybe = response as ApiEnvelope<T>;
    if (maybe.entity && typeof maybe.entity === "object") return maybe.entity;

    return null;
};

export const settingsProfilesApi = createApi({
    reducerPath: "settingsProfilesApi",
    baseQuery: privateBaseQuery(),
    tagTypes: ["SettingsProfiles", "AssignmentProctoringProfiles"],
    endpoints: (builder) => ({
        getSettingsProfiles: builder.query<
            SettingsProfile[],
            { type?: string } | void
        >({
            query: (arg) => ({
                url: "/settings-profiles",
                method: "GET",
                params: arg?.type ? { type: arg.type } : undefined,
            }),
            transformResponse: (response: unknown) => {
                const maybeEnvelope = response as { entities?: unknown } | null;
                const entities = maybeEnvelope?.entities;

                if (Array.isArray(entities)) {
                    return entities as SettingsProfile[];
                }

                if (Array.isArray(response)) {
                    return response as SettingsProfile[];
                }

                return [];
            },
            providesTags: (result) =>
                result
                    ? [
                        ...result.map((p) => ({
                            type: "SettingsProfiles" as const,
                            id: p.id,
                        })),
                        { type: "SettingsProfiles" as const, id: "LIST" },
                    ]
                    : [{ type: "SettingsProfiles" as const, id: "LIST" }],
        }),

        createSettingsProfile: builder.mutation<
            SettingsProfile,
            CreateSettingsProfilePayload
        >({
            query: (payload) => ({
                url: `/settings-profiles`,
                method: "POST",
                data: payload,
            }),
            transformResponse: (response: unknown) => {
                // API возвращает envelope { entity: {...} }
                return unwrapEntity<SettingsProfile>(response) ?? (response as SettingsProfile);
            },
            invalidatesTags: (result, error, payload) => {
                const tags: Array<
                    | { type: "SettingsProfiles"; id: number | "LIST" | "DEFAULTS" }
                    | { type: "AssignmentProctoringProfiles"; id: number }
                > = [{ type: "SettingsProfiles", id: "LIST" }];
                if (typeof payload.assignment_id === "number") {
                    tags.push({
                        type: "AssignmentProctoringProfiles",
                        id: payload.assignment_id,
                    });
                }
                return tags;
            },
        }),

        updateSettingsProfile: builder.mutation<
            SettingsProfile,
            UpdateSettingsProfilePayload
        >({
            query: ({
                id,
                apply_to_assignments = false,
                assignment_id,
                ...body
            }) => ({
                url: `/settings-profiles/${id}`,
                method: "PUT",
                params: { apply_to_assignments },
                data: body,
            }),
            transformResponse: (response: unknown) => {
                // API возвращает envelope { entity: {...} }
                return unwrapEntity<SettingsProfile>(response) ?? (response as SettingsProfile);
            },
            invalidatesTags: (result, error, { id, assignment_id }) => {
                const tags: Array<
                    | { type: "SettingsProfiles"; id: number | "LIST" | "DEFAULTS" }
                    | { type: "AssignmentProctoringProfiles"; id: number }
                > = [
                        { type: "SettingsProfiles", id },
                        { type: "SettingsProfiles", id: "LIST" },
                        { type: "SettingsProfiles", id: "DEFAULTS" },
                    ];
                if (typeof assignment_id === "number") {
                    tags.push({
                        type: "AssignmentProctoringProfiles",
                        id: assignment_id,
                    });
                }
                return tags;
            },
        }),

        deleteSettingsProfile: builder.mutation<void, number>({
            query: (id) => ({
                url: `/settings-profiles/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, id) => [
                { type: "SettingsProfiles", id },
                { type: "SettingsProfiles", id: "LIST" },
            ],
        }),
    }),
});

export const {
    useGetSettingsProfilesQuery,
    useCreateSettingsProfileMutation,
    useUpdateSettingsProfileMutation,
    useDeleteSettingsProfileMutation,
} = settingsProfilesApi;
